import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

let faceLandmarker: FaceLandmarker | null = null;
let modelsLoaded = false;
let loadingPromise: Promise<void> | null = null;

export interface FaceDetectionResult {
  detected: boolean;
  confidence?: number;
  box?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Head pose angles in degrees. yaw = left/right, pitch = up/down, roll = tilt. */
  headPose?: {
    yaw: number;
    pitch: number;
    roll: number;
  };
  /** Both eyes appear open and unobstructed (landmark geometry). */
  eyesVisible?: boolean;
  /** Glasses detected — either dark (blendshape) or any frame type (pixel analysis). */
  glassesSuspected?: boolean;
  /** Very little clearance above the face mesh — cap/hat cutting off top of head. */
  hatSuspected?: boolean;
}

/**
 * Load MediaPipe FaceLandmarker models (no-op if already loaded).
 * Loads WASM and model from the public/ folder — no internet required.
 */
export async function loadFaceDetectionModels(): Promise<void> {
  if (modelsLoaded) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    try {
      const wasmPath = `${process.env.PUBLIC_URL || ''}/mediapipe/wasm`;
      const modelPath = `${process.env.PUBLIC_URL || ''}/models/face_landmarker.task`;

      const vision = await FilesetResolver.forVisionTasks(wasmPath);
      faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: modelPath,
          delegate: 'GPU',
        },
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        runningMode: 'IMAGE',
        numFaces: 1,
      });
      modelsLoaded = true;
    } catch (err) {
      loadingPromise = null; // allow retry
      console.error('[FaceDetection] Failed to load MediaPipe:', err);
      throw err;
    }
  })();

  return loadingPromise;
}

export function areModelsLoaded(): boolean {
  return modelsLoaded;
}

/**
 * Decompose a row-major 4×4 rotation matrix from MediaPipe into
 * ZYX Euler angles (yaw, pitch, roll) in degrees.
 *
 * Convention:
 *  yaw   > 0 → face turned to the right
 *  pitch > 0 → face looking up
 *  roll  > 0 → head tilted to the right
 */
function extractHeadPose(data: number[] | Float32Array): { yaw: number; pitch: number; roll: number } {
  // Row-major: element at (row, col) = data[row*4 + col]
  // R[0] = data[0..3], R[1] = data[4..7], R[2] = data[8..11]
  const toDeg = (r: number) => (r * 180) / Math.PI;

  // ZYX decomposition: R = Rz(roll) * Ry(yaw) * Rx(pitch)
  // R[2][0] = -sin(yaw)
  const yaw   = toDeg(Math.asin(Math.max(-1, Math.min(1, -data[8]))));
  // R[2][1]/R[2][2] = sin(pitch)/cos(pitch) when cos(yaw) ≠ 0
  const pitch = toDeg(Math.atan2(data[9],  data[10]));
  // R[1][0]/R[0][0] = sin(roll)/cos(roll) when cos(yaw) ≠ 0
  const roll  = toDeg(Math.atan2(data[4],  data[0]));

  return { yaw, pitch, roll };
}

/**
 * Synthesise a strict 0–1 quality score suitable for biometric ID photos.
 * Penalties are intentionally aggressive — a frontal, unobstructed, well-sized
 * face should score ≥ 0.72; anything below is flagged as low quality.
 */
function synthesiseConfidence(
  box: { x: number; y: number; width: number; height: number },
  imgW: number,
  imgH: number,
  headPose: { yaw: number; pitch: number; roll: number },
  eyesVisible: boolean,
  glassesSuspected: boolean,
  hatSuspected: boolean,
): number {
  let score = 1.0;

  // Face coverage — face must be large enough to be useful for recognition
  const faceArea = (box.width * box.height) / (imgW * imgH);
  if      (faceArea < 0.05) score -= 0.55;
  else if (faceArea < 0.10) score -= 0.32;
  else if (faceArea < 0.15) score -= 0.14;

  // Yaw (horizontal rotation) — most important for frontal recognition
  const absYaw = Math.abs(headPose.yaw);
  if      (absYaw > 25) score -= 0.55;
  else if (absYaw > 15) score -= 0.38;
  else if (absYaw > 8)  score -= 0.18;

  // Pitch (vertical tilt)
  const absPitch = Math.abs(headPose.pitch);
  if      (absPitch > 20) score -= 0.42;
  else if (absPitch > 12) score -= 0.28;
  else if (absPitch > 6)  score -= 0.10;

  // Roll (head tilt left/right)
  const absRoll = Math.abs(headPose.roll);
  if      (absRoll > 18) score -= 0.32;
  else if (absRoll > 10) score -= 0.18;
  else if (absRoll > 5)  score -= 0.08;

  if (!eyesVisible)        score -= 0.35;
  if (glassesSuspected)    score -= 0.50;
  if (hatSuspected)        score -= 0.28;

  return Math.max(0.05, Math.min(1, score));
}

/**
 * Pixel-level glasses detection — nose bridge only.
 *
 * Per-eye checks are intentionally absent: eyebrows create the same gradient
 * signature as a glasses rim and cause systematic false positives.
 *
 * The nose bridge piece is anatomically unique: a thin (~2–4px at full res),
 * hard-edged, dark bar crossing between the inner eye corners. Bare skin at the
 * same location produces only a diffuse, low-contrast shadow.
 *
 * Three criteria must ALL pass:
 *   pmr  > 3.5  — peak row is 3.5× the mean (concentrated, not spread)
 *   maxG > 0.07 — absolute gradient large enough to be a physical edge
 *   wideRows ≤ 5 — the spike is narrow (frame ≤ 5/26 rows; skin shadow ≥ 6)
 *
 * console.debug logs real values to the Electron dev-tools console so
 * thresholds can be verified against test photos (Ctrl+Shift+I → Console,
 * filter "[GlassesBridge]").
 */
function detectGlassesByEdgePattern(
  img: HTMLImageElement | HTMLCanvasElement,
  lm: { x: number; y: number }[],
  imgW: number,
  imgH: number,
): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const liX     = lm[133].x * imgW;   // left inner canthus X
    const riX     = lm[362].x * imgW;   // right inner canthus X
    const liY     = lm[133].y * imgH;   // left inner canthus Y
    const riY     = lm[362].y * imgH;   // right inner canthus Y
    const bridgeY = (liY + riY) / 2;    // Y level where glasses bridge sits
    const bw      = riX - liX;
    if (bw < 6) return false;

    // Use 3× average eye-opening height so the bridge piece falls inside
    const leftEyeH  = Math.abs(lm[159].y - lm[145].y) * imgH;
    const rightEyeH = Math.abs(lm[386].y - lm[374].y) * imgH;
    const bh        = Math.max(16, ((leftEyeH + rightEyeH) / 2) * 3.5);

    const cW = 64, cH = 26;
    const c  = document.createElement('canvas');
    c.width  = cW; c.height = cH;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;

    // Slightly wider crop (12% padding each side) to handle landmark imprecision
    ctx.drawImage(img, liX - bw * 0.12, bridgeY - bh * 0.5, bw * 1.24, bh, 0, 0, cW, cH);
    const px  = ctx.getImageData(0, 0, cW, cH).data;
    const lum = new Float32Array(cW * cH);
    for (let i = 0; i < cW * cH; i++)
      lum[i] = (0.299 * px[i*4] + 0.587 * px[i*4+1] + 0.114 * px[i*4+2]) / 255;

    // Per-row mean absolute vertical gradient
    const rg = new Float32Array(cH);
    for (let y = 1; y < cH; y++) {
      let s = 0;
      for (let x = 0; x < cW; x++) s += Math.abs(lum[y*cW+x] - lum[(y-1)*cW+x]);
      rg[y] = s / cW;
    }

    let maxG = 0, sumG = 0, n = 0;
    for (let i = 1; i < cH; i++) { if (rg[i] > maxG) maxG = rg[i]; sumG += rg[i]; n++; }
    const pmr = maxG / (sumG / n + 0.001);

    // Narrowness: count rows whose gradient exceeds half the peak.
    // A thin physical frame produces 2–4 "wide" rows; diffuse shadows produce 6–12.
    let wideRows = 0;
    for (let i = 1; i < cH; i++) if (rg[i] > maxG * 0.5) wideRows++;

    console.debug('[GlassesBridge] pmr=', pmr.toFixed(2), 'maxG=', maxG.toFixed(3), 'wideRows=', wideRows);

    return pmr > 3.5 && maxG > 0.07 && wideRows <= 5;
  } catch {
    return false;
  }
}

/**
 * Detect the primary face in an image and return quality metrics.
 * Replaces the old face-api.js TinyFaceDetector with MediaPipe FaceLandmarker.
 * The public API is backward-compatible; callers using only `detected`, `confidence`,
 * and `box` will continue to work unchanged.
 */
export async function detectFace(
  imageElement: HTMLImageElement | HTMLCanvasElement,
): Promise<FaceDetectionResult> {
  if (!modelsLoaded || !faceLandmarker) {
    await loadFaceDetectionModels();
  }

  const imgW =
    imageElement instanceof HTMLImageElement
      ? imageElement.naturalWidth  || imageElement.width
      : imageElement.width;
  const imgH =
    imageElement instanceof HTMLImageElement
      ? imageElement.naturalHeight || imageElement.height
      : imageElement.height;

  if (!imgW || !imgH) return { detected: false };

  const result = faceLandmarker!.detect(imageElement);

  if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
    return { detected: false };
  }

  const landmarks = result.faceLandmarks[0];

  // Compute bounding box from landmarks (normalized 0-1 → pixels)
  const xs = landmarks.map(l => l.x * imgW);
  const ys = landmarks.map(l => l.y * imgH);
  const minX = Math.max(0, Math.min(...xs));
  const minY = Math.max(0, Math.min(...ys));
  const maxX = Math.min(imgW, Math.max(...xs));
  const maxY = Math.min(imgH, Math.max(...ys));
  const box = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };

  // Head pose from transformation matrix
  let headPose = { yaw: 0, pitch: 0, roll: 0 };
  if (result.facialTransformationMatrixes?.length > 0) {
    headPose = extractHeadPose(result.facialTransformationMatrixes[0].data);
  }

  // ── Eye visibility from landmark geometry ────────────────────────────────
  // Left eye outer(33)/inner(133), right eye inner(362)/outer(263)
  const leftEyeW  = Math.abs(landmarks[133].x - landmarks[33].x)  * imgW;
  const rightEyeW = Math.abs(landmarks[263].x - landmarks[362].x) * imgW;
  // Also check eye openness: vertical span between upper (159/386) and lower (145/374) eyelid
  const leftEyeH  = Math.abs(landmarks[159].y - landmarks[145].y) * imgH;
  const rightEyeH = Math.abs(landmarks[386].y - landmarks[374].y) * imgH;
  const leftOpen  = leftEyeW  > 2 && (leftEyeH  / (leftEyeW  || 1)) > 0.04;
  const rightOpen = rightEyeW > 2 && (rightEyeH / (rightEyeW || 1)) > 0.04;
  const eyesVisible = leftOpen && rightOpen;

  // ── Glasses detection: blendshapes (dark glasses) + pixel analysis (any frames) ──
  // Signal 1: dark glasses cause both eyes to appear "closed" in blendshapes
  const blendshapes = result.faceBlendshapes?.[0]?.categories ?? [];
  const getBlend = (name: string) =>
    blendshapes.find(b => b.categoryName === name)?.score ?? 0;
  const eyeBlinkLeft  = getBlend('eyeBlinkLeft');
  const eyeBlinkRight = getBlend('eyeBlinkRight');
  const leftBlinkLandmark  = leftEyeW  > 2 ? leftEyeH  / leftEyeW  : 0;
  const rightBlinkLandmark = rightEyeW > 2 ? rightEyeH / rightEyeW : 0;
  const darkGlasses = (eyeBlinkLeft > 0.42 || leftBlinkLandmark < 0.03)
                   && (eyeBlinkRight > 0.42 || rightBlinkLandmark < 0.03);
  // Signal 2: pixel-level edge analysis for any frame type (clear, wire, thick)
  const frameGlasses = detectGlassesByEdgePattern(imageElement, landmarks, imgW, imgH);
  const glassesSuspected = darkGlasses || frameGlasses;

  // ── Hat / cap detection from landmark clearance ───────────────────────────
  // Compute min/max Y in normalized coords (independent of pixel bbox above)
  let lmMinY = Infinity, lmMaxY = -Infinity;
  for (const l of landmarks) {
    if (l.y < lmMinY) lmMinY = l.y;
    if (l.y > lmMaxY) lmMaxY = l.y;
  }
  const faceHeightNorm = lmMaxY - lmMinY;
  // clearanceRatio = space above face mesh ÷ face mesh height (both normalized)
  const clearanceRatio = faceHeightNorm > 0 ? lmMinY / faceHeightNorm : 1;
  // Threshold 0.12: less than 12% of face-height of clearance → cap likely cutting off top
  const hatSuspected = clearanceRatio < 0.12;

  const confidence = synthesiseConfidence(
    box, imgW, imgH, headPose, eyesVisible, glassesSuspected, hatSuspected,
  );

  return { detected: true, confidence, box, headPose, eyesVisible, glassesSuspected, hatSuspected };
}

// ─── Crop helpers (unchanged — used by photo capture/upload modals) ────────────

/**
 * Calculate crop area with padding around detected face.
 * Returns crop coordinates in pixels.
 */
export function calculateFaceCropArea(
  faceBox: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
  paddingPercent: number = 0.4,
): { x: number; y: number; width: number; height: number } {
  const paddingX = faceBox.width  * paddingPercent;
  const paddingY = faceBox.height * paddingPercent;

  let cropX      = faceBox.x - paddingX;
  let cropY      = faceBox.y - paddingY;
  let cropWidth  = faceBox.width  + paddingX * 2;
  let cropHeight = faceBox.height + paddingY * 2;

  const size    = Math.max(cropWidth, cropHeight);
  const centerX = faceBox.x + faceBox.width  / 2;
  const centerY = faceBox.y + faceBox.height / 2;

  cropX      = Math.max(0, centerX - size / 2);
  cropY      = Math.max(0, centerY - size / 2);
  cropWidth  = size;
  cropHeight = size;

  if (cropX + cropWidth  > imageWidth)  cropWidth  = imageWidth  - cropX;
  if (cropY + cropHeight > imageHeight) cropHeight = imageHeight - cropY;

  return { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
}

/**
 * Convert pixel-based crop area to react-easy-crop format
 * (center offset in pixels + zoom level).
 */
export function convertToEasyCropFormat(
  cropArea: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
): { crop: { x: number; y: number }; zoom: number } {
  const cropCenterX = cropArea.x + cropArea.width  / 2;
  const cropCenterY = cropArea.y + cropArea.height / 2;
  const offsetX     = cropCenterX - imageWidth  / 2;
  const offsetY     = cropCenterY - imageHeight / 2;
  const zoom        = Math.min(imageWidth, imageHeight) / Math.min(cropArea.width, cropArea.height);

  return {
    crop: { x: -offsetX, y: -offsetY },
    zoom: Math.max(1, zoom),
  };
}
